import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Rss, Plus, Pencil, Trash2, Wallet, XCircle, Radio as RadioIcon, ExternalLink,
  AlertTriangle, ClipboardList,
} from "lucide-react";
import { api, idr, fmtNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDataQuery } from "@/hooks/useDataQuery";
import { useDebounce } from "@/hooks/useDebounce";
import LoadingState from "@/components/ui/loading-state";
import InputRupiah from "@/components/InputRupiah";
import { DatePicker } from "@/components/ui/date-picker";
import { formatTanggal, hariIniISO } from "@/lib/tanggal";
import {
  PageHeader, TidakPunyaAkses, SearchInput, StatusBadge, PLACEMENT_STATUS_META,
  PFP_PLATFORMS, PLATFORM_LABEL, SortTh, useSortState, sortRows, usePaginasi, Paginasi,
  unduhCsv, TombolEksporCsv, Modal, Field, inputCls, selectCls, textareaCls,
  useAkunKasBank, useCampaignMap, namaCampaign, usePinHapus, PinHapusDialog,
} from "./_shared";

// ============ Dua sumbu "Management" untuk placement (brief owner Update 2) ============
// Placement hanya punya DUA dari tiga sumbu: Payment (turunan uang yang tercatat
// di backend) dan Report (bukti tayang). Approval SENGAJA tidak ada — placement
// tidak pernah melewati persetujuan siapa pun; persetujuan belanjanya hidup di
// baris Beban Marketing yang menautkan placement ini.
// Label-label ini kembar dengan MarketingExpense.jsx: `_shared.jsx` adalah
// tempat yang benar untuk menyatukannya, tapi berkas itu di luar cakupan batch
// ini. Nilai statusnya sendiri tetap SATU sumber — dihitung di backend.
const PAYMENT_STATUS_META = {
  pending: { label: "Belum Dibayar", chip: "bg-slate-100 text-slate-600" },
  partial: { label: "Sebagian", chip: "bg-amber-100 text-amber-800" },
  paid: { label: "Dibayar", chip: "bg-emerald-100 text-emerald-800" },
};
const REPORT_STATUS_META = {
  waiting: { label: "Menunggu Laporan", chip: "bg-slate-100 text-slate-600" },
  submitted: { label: "Diserahkan", chip: "bg-sky-100 text-sky-700" },
  reviewed: { label: "Ditinjau", chip: "bg-indigo-100 text-indigo-700" },
  completed: { label: "Selesai", chip: "bg-emerald-600 text-white" },
};
// Cermin PERSIS `REPORT_TRANSISI` backend — supaya tombol yang pasti ditolak
// server tidak pernah ditampilkan (rule #3 CLAUDE.md).
const REPORT_TRANSISI = {
  waiting: ["submitted"],
  submitted: ["reviewed", "waiting"],
  reviewed: ["completed", "submitted"],
  completed: [],
};
const statusLaporan = (row) => (REPORT_STATUS_META[row?.report_status] ? row.report_status : "waiting");

const emptyAkun = () => ({ nama_akun: "", platform: PFP_PLATFORMS[0], followers: 0, rate_card: "", kontak: "", catatan: "" });

// ============ Modal status laporan placement (sumbu Report) ============
// Pilihan diturunkan dari `REPORT_TRANSISI` (cermin backend), dan dua syarat
// backend lain dicerminkan di sini supaya tidak ada tombol yang berujung 400:
// bukti tayang wajib untuk "Diserahkan", dan peninjau bukan penyerahnya.
function LaporanPlacementModal({ placement, user, onClose, onSuccess }) {
  const sekarang = statusLaporan(placement);
  const opsi = REPORT_TRANSISI[sekarang] || [];
  const akuPenyerah = !!user?.email && user.email === placement.report_submitted_by;
  const buktiKosong = !(placement.bukti_url || "").trim();
  const terkunci = (tujuan) =>
    (tujuan === "reviewed" && akuPenyerah) || (tujuan === "submitted" && buktiKosong);
  const [status, setStatus] = useState(opsi.find((o) => !terkunci(o)) || "");
  const [catatan, setCatatan] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!status) { toast.error("Pilih status laporan tujuan"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/marketing/pfp/placements/${placement.id}/report`, { status, catatan });
      toast.success(r.data.message || "Status laporan diperbarui");
      onSuccess();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah status laporan"); }
    finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Status Laporan Placement" maxWidth="max-w-md" testid="mkt-pfp-laporan-modal"
           footer={<>
             <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Batal</button>
             <button onClick={submit} disabled={busy || !status} data-testid="mkt-pfp-laporan-submit"
                     className="h-10 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium disabled:opacity-50">
               {busy ? "Memproses..." : "Simpan"}
             </button>
           </>}>
      <p className="text-sm text-slate-600">
        <b>{placement.code}</b> — {placement.pfp_nama_akun}<br />
        Status laporan sekarang: <StatusBadge meta={REPORT_STATUS_META} value={sekarang} />
      </p>
      <Field label="Pindahkan ke" required>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="mkt-pfp-laporan-status" className={selectCls}>
          <option value="">— pilih status —</option>
          {opsi.map((o) => (
            <option key={o} value={o} disabled={terkunci(o)}>
              {REPORT_STATUS_META[o].label}
              {o === "submitted" && buktiKosong ? " (bukti tayang belum diisi)" : ""}
              {o === "reviewed" && akuPenyerah ? " (Anda yang menyerahkan — peninjau harus orang lain)" : ""}
            </option>
          ))}
        </select>
      </Field>
      {buktiKosong && opsi.includes("submitted") && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5 inline-flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          Bukti tayang (URL) belum diisi. Lengkapi lewat tombol Edit placement dulu — laporan tanpa bukti
          tayang tidak melaporkan apa pun, dan server akan menolaknya.
        </p>
      )}
      <Field label="Catatan" hint="Tersimpan di riwayat laporan beserta nama dan waktunya.">
        <textarea value={catatan} rows={2} onChange={(e) => setCatatan(e.target.value)} data-testid="mkt-pfp-laporan-catatan" className={textareaCls} />
      </Field>
      {(placement.report_riwayat || []).length > 0 && (
        <div className="text-[11px] text-slate-500 border border-slate-200 rounded-lg p-2.5 space-y-1 max-h-32 overflow-y-auto" data-testid="mkt-pfp-laporan-riwayat">
          <p className="font-semibold text-slate-600">Riwayat</p>
          {(placement.report_riwayat || []).map((r, i) => (
            <p key={i}>
              {REPORT_STATUS_META[r.dari]?.label || r.dari} → {REPORT_STATUS_META[r.ke]?.label || r.ke}
              {" · "}{r.by_name || r.by}{r.at ? ` · ${formatTanggal(r.at.slice(0, 10))}` : ""}
              {r.catatan ? ` — ${r.catatan}` : ""}
            </p>
          ))}
        </div>
      )}
    </Modal>
  );
}

export default function Pfp() {
  const { user, can } = useAuth();
  const bolehLihat = can("marketing_pfp", "view");
  const bolehTambah = can("marketing_pfp", "create");
  const bolehUpdate = can("marketing_pfp", "update");
  const bolehHapus = can("marketing_pfp", "delete");
  const bolehCampaign = can("marketing_campaign", "view");
  const { campaignMap } = useCampaignMap(bolehCampaign);
  const akunKasBank = useAkunKasBank();
  const [searchParams] = useSearchParams();
  const campaignPreset = searchParams.get("campaign_id") || "";

  const [tab, setTab] = useState(campaignPreset ? "placement" : "akun");

  // ---- Akun PFP ----
  const [qAkun, setQAkun] = useState("");
  const qAkunDebounced = useDebounce(qAkun, 350);
  const [platformFilter, setPlatformFilter] = useState("");
  const { sortKey: sortAkunKey, sortDir: sortAkunDir, toggle: toggleAkunSort } = useSortState("created_at", "desc");
  const [formAkun, setFormAkun] = useState(null);
  const [editAkunId, setEditAkunId] = useState(null);
  const [busy, setBusy] = useState(false);
  const { dialog: pinDialog, setDialog: setPinDialog, hapusDenganPin, submitDialog } = usePinHapus();

  const { data: akunData, loading: loadingAkun, error: errorAkun, refetch: refetchAkun } = useDataQuery(
    ["marketing", "pfp-accounts", platformFilter],
    () => api.get("/marketing/pfp/accounts", { params: platformFilter ? { platform: platformFilter } : {} }).then((r) => r.data),
    { enabled: bolehLihat },
  );
  const akunRows = useMemo(() => akunData || [], [akunData]);
  const akunFiltered = useMemo(() => {
    const t = qAkunDebounced.trim().toLowerCase();
    if (!t) return akunRows;
    return akunRows.filter((a) => a.nama_akun?.toLowerCase().includes(t));
  }, [akunRows, qAkunDebounced]);
  const akunSorted = sortRows(akunFiltered, sortAkunKey, sortAkunDir);
  const akunPag = usePaginasi(akunSorted.length, 15);
  const akunHalaman = akunSorted.slice(akunPag.awal, akunPag.akhir);

  const bukaTambahAkun = () => { setEditAkunId(null); setFormAkun(emptyAkun()); };
  const bukaEditAkun = (a) => {
    setEditAkunId(a.id);
    setFormAkun({ nama_akun: a.nama_akun, platform: a.platform, followers: a.followers || 0, rate_card: a.rate_card || "", kontak: a.kontak || "", catatan: a.catatan || "" });
  };
  const simpanAkun = async () => {
    if (!formAkun.nama_akun.trim()) { toast.error("Nama akun wajib diisi"); return; }
    setBusy(true);
    try {
      if (editAkunId) { await api.patch(`/marketing/pfp/accounts/${editAkunId}`, formAkun); toast.success("Akun PFP diperbarui"); }
      else { await api.post("/marketing/pfp/accounts", formAkun); toast.success("Akun PFP ditambahkan"); }
      setFormAkun(null); setEditAkunId(null); refetchAkun();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan akun PFP"); }
    finally { setBusy(false); }
  };
  const hapusAkun = async (a) => {
    if (!window.confirm(`Hapus akun PFP "${a.nama_akun}"?`)) return;
    const aksi = (pin) => api.delete(`/marketing/pfp/accounts/${a.id}`, pin ? { params: { pin } } : undefined);
    const langsung = await hapusDenganPin(aksi, { modul: "marketing_pfp", targetId: a.id, ringkasan: `Hapus akun PFP ${a.nama_akun}` });
    if (langsung) { toast.success("Akun PFP dihapus"); refetchAkun(); }
  };

  // ---- Placement ----
  const [qPlacement, setQPlacement] = useState("");
  const qPlacementDebounced = useDebounce(qPlacement, 350);
  const [statusFilter, setStatusFilter] = useState("");
  const [campaignFilter, setCampaignFilter] = useState(campaignPreset);
  // Dikirim KE SERVER (daftar dipotong 2000 baris di backend) — menyaringnya
  // di layar akan membuat baris ke-2001 "tidak ada hasil" padahal ada.
  const [paymentFilter, setPaymentFilter] = useState("");
  const [reportFilter, setReportFilter] = useState("");
  const { sortKey: sortPlKey, sortDir: sortPlDir, toggle: togglePlSort } = useSortState("tanggal_tayang", "desc");
  const [formPlacement, setFormPlacement] = useState(null);
  const [editPlacementId, setEditPlacementId] = useState(null);
  const [bayarState, setBayarState] = useState(null);
  const [laporanTarget, setLaporanTarget] = useState(null);

  const { data: plData, loading: loadingPl, error: errorPl, refetch: refetchPl } = useDataQuery(
    ["marketing", "pfp-placements", statusFilter, campaignFilter, paymentFilter, reportFilter],
    () => api.get("/marketing/pfp/placements", {
      params: {
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(campaignFilter ? { campaign_id: campaignFilter } : {}),
        ...(paymentFilter ? { payment_status: paymentFilter } : {}),
        ...(reportFilter ? { report_status: reportFilter } : {}),
      },
    }).then((r) => r.data),
    { enabled: bolehLihat },
  );
  const plFiltered = useMemo(() => {
    const plRows = plData || [];
    const t = qPlacementDebounced.trim().toLowerCase();
    if (!t) return plRows;
    return plRows.filter((p) => p.pfp_nama_akun?.toLowerCase().includes(t) || p.code?.toLowerCase().includes(t));
  }, [plData, qPlacementDebounced]);
  const plSorted = sortRows(plFiltered, sortPlKey, sortPlDir);
  const plPag = usePaginasi(plSorted.length, 15);
  const plHalaman = plSorted.slice(plPag.awal, plPag.akhir);

  const eksporPlacementCsv = () => {
    unduhCsv(`pfp-placement-${hariIniISO()}.csv`, [
      { label: "Kode", value: (r) => r.code },
      { label: "Akun PFP", value: (r) => r.pfp_nama_akun },
      { label: "Campaign", value: (r) => namaCampaign(campaignMap, r.campaign_id) },
      { label: "Tanggal Tayang", value: (r) => r.tanggal_tayang },
      { label: "Nominal", value: (r) => r.nominal },
      { label: "Status", value: (r) => PLACEMENT_STATUS_META[r.status]?.label || r.status },
      { label: "Payment", value: (r) => PAYMENT_STATUS_META[r.payment_status]?.label || r.payment_status || "" },
      { label: "Report", value: (r) => REPORT_STATUS_META[statusLaporan(r)].label },
    ], plSorted);
  };

  const bukaTambahPlacement = () => {
    setEditPlacementId(null);
    setFormPlacement({ pfp_account_id: akunRows[0]?.id || "", campaign_id: campaignFilter || "", tanggal_tayang: hariIniISO(), nominal: 0, bukti_url: "", catatan: "" });
  };
  const bukaEditPlacement = (p) => {
    setEditPlacementId(p.id);
    setFormPlacement({ pfp_account_id: p.pfp_account_id, campaign_id: p.campaign_id || "", tanggal_tayang: p.tanggal_tayang, nominal: p.nominal, bukti_url: p.bukti_url || "", catatan: p.catatan || "" });
  };
  const simpanPlacement = async () => {
    if (!formPlacement.pfp_account_id) { toast.error("Pilih akun PFP dulu"); return; }
    if (!formPlacement.nominal || formPlacement.nominal <= 0) { toast.error("Nominal wajib lebih dari 0"); return; }
    setBusy(true);
    try {
      if (editPlacementId) {
        await api.patch(`/marketing/pfp/placements/${editPlacementId}`, {
          tanggal_tayang: formPlacement.tanggal_tayang, nominal: formPlacement.nominal,
          bukti_url: formPlacement.bukti_url, catatan: formPlacement.catatan,
        });
        toast.success("Placement diperbarui");
      } else {
        await api.post("/marketing/pfp/placements", { ...formPlacement, campaign_id: formPlacement.campaign_id || null });
        toast.success("Placement ditambahkan");
      }
      setFormPlacement(null); setEditPlacementId(null); refetchPl();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan placement"); }
    finally { setBusy(false); }
  };

  const tandaiTayang = async (p) => {
    try { await api.patch(`/marketing/pfp/placements/${p.id}`, { status: "tayang" }); toast.success("Placement ditandai tayang"); refetchPl(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah status"); }
  };
  const batalkan = async (p) => {
    if (!window.confirm(`Batalkan placement ${p.code}?`)) return;
    try { await api.patch(`/marketing/pfp/placements/${p.id}`, { status: "dibatalkan" }); toast.success("Placement dibatalkan"); refetchPl(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal membatalkan placement"); }
  };
  const hapusPlacement = async (p) => {
    if (!window.confirm(`Hapus placement ${p.code}?`)) return;
    const aksi = (pin) => api.delete(`/marketing/pfp/placements/${p.id}`, pin ? { params: { pin } } : undefined);
    const langsung = await hapusDenganPin(aksi, { modul: "marketing_pfp", targetId: p.id, ringkasan: `Hapus placement ${p.code}` });
    if (langsung) { toast.success("Placement dihapus"); refetchPl(); }
  };
  /** Tombol Laporan hanya muncul kalau ADA perpindahan yang sah dan placement
   *  belum dibatalkan — dua kondisi yang sama persis dipakai backend menolak. */
  const bisaUbahLaporan = (pl) =>
    bolehUpdate && pl.status !== "dibatalkan" && (REPORT_TRANSISI[statusLaporan(pl)] || []).length > 0;

  const submitBayar = async () => {
    if (!bayarState?.sumber_akun_id) { toast.error("Pilih akun sumber pembayaran"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/marketing/pfp/placements/${bayarState.placement.id}/bayar`, {
        sumber_akun_id: bayarState.sumber_akun_id, tanggal: bayarState.tanggal || hariIniISO(),
      });
      toast.success(r.data.message || "Placement dibayar");
      setBayarState(null); refetchPl();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mencatat pembayaran"); }
    finally { setBusy(false); }
  };

  if (!bolehLihat) {
    return (
      <div className="p-4 md:p-8 space-y-5">
        <PageHeader icon={Rss} title="PFP & Paid Promote" />
        <TidakPunyaAkses label="PFP & Paid Promote" testid="mkt-pfp-tanpa-akses" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="mkt-pfp">
      <PageHeader icon={Rss} title="PFP & Paid Promote" desc="Akun paid feature post/meme/komunitas dan riwayat placement berbayarnya." />

      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 gap-1 w-fit">
        <button onClick={() => setTab("akun")} data-testid="mkt-pfp-tab-akun"
                className={`px-4 h-9 rounded-md text-sm font-medium inline-flex items-center gap-1.5 ${tab === "akun" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          <RadioIcon className="w-4 h-4" /> Akun PFP
        </button>
        <button onClick={() => setTab("placement")} data-testid="mkt-pfp-tab-placement"
                className={`px-4 h-9 rounded-md text-sm font-medium inline-flex items-center gap-1.5 ${tab === "placement" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          <Rss className="w-4 h-4" /> Placement
        </button>
      </div>

      {tab === "akun" ? (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <SearchInput value={qAkun} onChange={setQAkun} placeholder="Cari nama akun..." testid="mkt-pfp-akun-cari" className="w-56" />
              <select value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)} data-testid="mkt-pfp-akun-filter-platform"
                      className={`${selectCls} h-9 w-auto`}>
                <option value="">Semua Platform</option>
                {PFP_PLATFORMS.map((p) => <option key={p} value={p}>{PLATFORM_LABEL[p]}</option>)}
              </select>
            </div>
            {bolehTambah && (
              <button onClick={bukaTambahAkun} data-testid="mkt-pfp-akun-tambah"
                      className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
                <Plus className="w-4 h-4" /> Tambah Akun PFP
              </button>
            )}
          </div>
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <SortTh label="Nama Akun" sortKeyName="nama_akun" sortKey={sortAkunKey} sortDir={sortAkunDir} onSort={toggleAkunSort} testid="mkt-pfp-akun-sort-nama" />
                    <th className="px-3 py-2.5 font-semibold">Platform</th>
                    <SortTh label="Followers" sortKeyName="followers" sortKey={sortAkunKey} sortDir={sortAkunDir} onSort={toggleAkunSort} align="right" testid="mkt-pfp-akun-sort-followers" />
                    <th className="px-3 py-2.5 font-semibold">Rate Card</th>
                    <th className="px-3 py-2.5 font-semibold">Kontak</th>
                    <th className="px-3 py-2.5 font-semibold text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <LoadingState loading={loadingAkun} error={errorAkun} onRetry={refetchAkun} kosong={!loadingAkun && !errorAkun && akunHalaman.length === 0}
                                varian="tabel" kolom={6} pesanKosong="Belum ada akun PFP." data-testid="mkt-pfp-akun-state">
                    {akunHalaman.map((a) => (
                      <tr key={a.id} className="hover:bg-slate-50/60" data-testid={`mkt-pfp-akun-row-${a.id}`}>
                        <td className="px-3 py-2.5 font-medium text-slate-900">{a.nama_akun}{!a.is_active && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">Nonaktif</span>}</td>
                        <td className="px-3 py-2.5 text-slate-600">{PLATFORM_LABEL[a.platform] || a.platform}</td>
                        <td className="px-3 py-2.5 text-right font-mono">{fmtNum(a.followers)}</td>
                        <td className="px-3 py-2.5 text-slate-500 text-xs">{a.rate_card || "-"}</td>
                        <td className="px-3 py-2.5 text-slate-500 text-xs">{a.kontak || "-"}</td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center justify-end gap-1">
                            {bolehUpdate && (
                              <button onClick={() => bukaEditAkun(a)} data-testid={`mkt-pfp-akun-edit-${a.id}`}
                                      className="w-8 h-8 rounded hover:bg-slate-100 text-slate-500 grid place-items-center"><Pencil className="w-4 h-4" /></button>
                            )}
                            {bolehHapus && (
                              <button onClick={() => hapusAkun(a)} data-testid={`mkt-pfp-akun-hapus-${a.id}`}
                                      className="w-8 h-8 rounded hover:bg-rose-50 text-rose-600 grid place-items-center"><Trash2 className="w-4 h-4" /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </LoadingState>
                </tbody>
              </table>
            </div>
            <Paginasi page={akunPag.page} setPage={akunPag.setPage} totalPages={akunPag.totalPages} testid="mkt-pfp-akun-paginasi" />
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <SearchInput value={qPlacement} onChange={setQPlacement} placeholder="Cari kode/akun..." testid="mkt-pfp-pl-cari" className="w-56" />
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="mkt-pfp-pl-filter-status"
                      className={`${selectCls} h-9 w-auto`}>
                <option value="">Semua Status</option>
                {Object.entries(PLACEMENT_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              {bolehCampaign && (
                <select value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)} data-testid="mkt-pfp-pl-filter-campaign"
                        className={`${selectCls} h-9 w-auto`}>
                  <option value="">Semua Campaign</option>
                  {Object.values(campaignMap).map((c) => <option key={c.id} value={c.id}>{c.code} — {c.nama}</option>)}
                </select>
              )}
              <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)} data-testid="mkt-pfp-pl-filter-payment"
                      className={`${selectCls} h-9 w-auto`}>
                <option value="">Semua Payment</option>
                {Object.entries(PAYMENT_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <select value={reportFilter} onChange={(e) => setReportFilter(e.target.value)} data-testid="mkt-pfp-pl-filter-report"
                      className={`${selectCls} h-9 w-auto`}>
                <option value="">Semua Report</option>
                {Object.entries(REPORT_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <TombolEksporCsv onClick={eksporPlacementCsv} testid="mkt-pfp-pl-export" />
              {bolehTambah && (
                <button onClick={bukaTambahPlacement} disabled={akunRows.length === 0} data-testid="mkt-pfp-pl-tambah"
                        className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
                  <Plus className="w-4 h-4" /> Tambah Placement
                </button>
              )}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2.5 font-semibold">Kode / Akun</th>
                    <th className="px-3 py-2.5 font-semibold">Campaign</th>
                    <SortTh label="Tanggal Tayang" sortKeyName="tanggal_tayang" sortKey={sortPlKey} sortDir={sortPlDir} onSort={togglePlSort} testid="mkt-pfp-pl-sort-tanggal" />
                    <SortTh label="Nominal" sortKeyName="nominal" sortKey={sortPlKey} sortDir={sortPlDir} onSort={togglePlSort} align="right" testid="mkt-pfp-pl-sort-nominal" />
                    <th className="px-3 py-2.5 font-semibold">Status</th>
                    <th className="px-3 py-2.5 font-semibold">Payment</th>
                    <th className="px-3 py-2.5 font-semibold">Report</th>
                    <th className="px-3 py-2.5 font-semibold text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <LoadingState loading={loadingPl} error={errorPl} onRetry={refetchPl} kosong={!loadingPl && !errorPl && plHalaman.length === 0}
                                varian="tabel" kolom={8} pesanKosong="Belum ada placement." data-testid="mkt-pfp-pl-state">
                    {plHalaman.map((p) => (
                      <tr key={p.id} className="hover:bg-slate-50/60" data-testid={`mkt-pfp-pl-row-${p.id}`}>
                        <td className="px-3 py-2.5">
                          <p className="font-mono text-[11px] text-slate-400">{p.code}</p>
                          <p className="text-slate-900 font-medium">{p.pfp_nama_akun}</p>
                        </td>
                        <td className="px-3 py-2.5 text-slate-600 text-xs">{namaCampaign(campaignMap, p.campaign_id)}</td>
                        <td className="px-3 py-2.5 text-slate-600">{formatTanggal(p.tanggal_tayang)}</td>
                        <td className="px-3 py-2.5 text-right font-mono">{idr(p.nominal)}</td>
                        <td className="px-3 py-2.5"><StatusBadge meta={PLACEMENT_STATUS_META} value={p.status} /></td>
                        <td className="px-3 py-2.5" data-testid={`mkt-pfp-pl-payment-${p.id}`}>
                          <StatusBadge meta={PAYMENT_STATUS_META} value={p.payment_status} />
                        </td>
                        <td className="px-3 py-2.5" data-testid={`mkt-pfp-pl-report-${p.id}`}>
                          <div className="flex flex-col items-start gap-1">
                            <StatusBadge meta={REPORT_STATUS_META} value={statusLaporan(p)} />
                            {statusLaporan(p) === "waiting" && !p.bukti_url && (
                              <span className="text-[10px] text-amber-700 inline-flex items-center gap-0.5">
                                <AlertTriangle className="w-2.5 h-2.5 shrink-0" /> bukti tayang kosong
                              </span>
                            )}
                            {p.report_status_by_name && (
                              <span className="text-[10px] text-slate-400">
                                {p.report_status_by_name}{p.report_status_at ? ` · ${formatTanggal(p.report_status_at.slice(0, 10))}` : ""}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center justify-end gap-1 flex-wrap">
                            {bolehUpdate && p.status === "rencana" && (
                              <button onClick={() => tandaiTayang(p)} data-testid={`mkt-pfp-pl-tayang-${p.id}`}
                                      className="px-2 h-7 rounded bg-sky-50 hover:bg-sky-100 text-sky-700 text-[11px] font-medium">Tandai Tayang</button>
                            )}
                            {bolehUpdate && ["rencana", "tayang"].includes(p.status) && (
                              <button onClick={() => setBayarState({ placement: p, sumber_akun_id: "", tanggal: hariIniISO() })}
                                      data-testid={`mkt-pfp-pl-bayar-${p.id}`}
                                      className="px-2 h-7 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[11px] font-medium inline-flex items-center gap-1">
                                <Wallet className="w-3 h-3" /> Bayar
                              </button>
                            )}
                            {bisaUbahLaporan(p) && (
                              <button onClick={() => setLaporanTarget(p)} data-testid={`mkt-pfp-pl-laporan-${p.id}`}
                                      title="Ubah status laporan bukti tayang placement ini"
                                      className="px-2 h-7 rounded bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-[11px] font-medium inline-flex items-center gap-1">
                                <ClipboardList className="w-3 h-3" /> Laporan
                              </button>
                            )}
                            {bolehUpdate && p.status !== "selesai" && (
                              <button onClick={() => bukaEditPlacement(p)} data-testid={`mkt-pfp-pl-edit-${p.id}`}
                                      className="w-7 h-7 rounded hover:bg-slate-100 text-slate-500 grid place-items-center"><Pencil className="w-3.5 h-3.5" /></button>
                            )}
                            {bolehUpdate && ["rencana", "tayang"].includes(p.status) && (
                              <button onClick={() => batalkan(p)} data-testid={`mkt-pfp-pl-batal-${p.id}`}
                                      className="w-7 h-7 rounded hover:bg-rose-50 text-rose-600 grid place-items-center"><XCircle className="w-3.5 h-3.5" /></button>
                            )}
                            {bolehHapus && p.status !== "selesai" && (
                              <button onClick={() => hapusPlacement(p)} data-testid={`mkt-pfp-pl-hapus-${p.id}`}
                                      className="w-7 h-7 rounded hover:bg-rose-50 text-rose-600 grid place-items-center"><Trash2 className="w-3.5 h-3.5" /></button>
                            )}
                            {p.bukti_url && (
                              <a href={p.bukti_url} target="_blank" rel="noreferrer" data-testid={`mkt-pfp-pl-bukti-${p.id}`}
                                 className="w-7 h-7 rounded hover:bg-slate-100 text-slate-500 grid place-items-center"><ExternalLink className="w-3.5 h-3.5" /></a>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </LoadingState>
                </tbody>
              </table>
            </div>
            <Paginasi page={plPag.page} setPage={plPag.setPage} totalPages={plPag.totalPages} testid="mkt-pfp-pl-paginasi" />
          </div>
        </>
      )}

      {/* Form akun PFP */}
      <Modal open={!!formAkun} onClose={() => { setFormAkun(null); setEditAkunId(null); }} title={editAkunId ? "Edit Akun PFP" : "Tambah Akun PFP"} testid="mkt-pfp-akun-modal"
             footer={<>
               <button onClick={() => { setFormAkun(null); setEditAkunId(null); }} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Batal</button>
               <button onClick={simpanAkun} disabled={busy} data-testid="mkt-pfp-akun-simpan"
                       className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">{busy ? "Menyimpan..." : "Simpan"}</button>
             </>}>
        {formAkun && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nama Akun" required>
                <input value={formAkun.nama_akun} onChange={(e) => setFormAkun((f) => ({ ...f, nama_akun: e.target.value }))} data-testid="mkt-pfp-akun-f-nama" className={inputCls} />
              </Field>
              <Field label="Platform" required>
                <select value={formAkun.platform} onChange={(e) => setFormAkun((f) => ({ ...f, platform: e.target.value }))} data-testid="mkt-pfp-akun-f-platform" className={selectCls}>
                  {PFP_PLATFORMS.map((p) => <option key={p} value={p}>{PLATFORM_LABEL[p]}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Followers">
              <input type="number" min="0" value={formAkun.followers} onChange={(e) => setFormAkun((f) => ({ ...f, followers: Number(e.target.value || 0) }))} data-testid="mkt-pfp-akun-f-followers" className={inputCls} />
            </Field>
            <Field label="Rate Card">
              <input value={formAkun.rate_card} onChange={(e) => setFormAkun((f) => ({ ...f, rate_card: e.target.value }))} data-testid="mkt-pfp-akun-f-rate" className={inputCls} />
            </Field>
            <Field label="Kontak">
              <input value={formAkun.kontak} onChange={(e) => setFormAkun((f) => ({ ...f, kontak: e.target.value }))} data-testid="mkt-pfp-akun-f-kontak" className={inputCls} />
            </Field>
            <Field label="Catatan">
              <textarea value={formAkun.catatan} rows={2} onChange={(e) => setFormAkun((f) => ({ ...f, catatan: e.target.value }))} data-testid="mkt-pfp-akun-f-catatan" className={textareaCls} />
            </Field>
          </>
        )}
      </Modal>

      {/* Form placement */}
      <Modal open={!!formPlacement} onClose={() => { setFormPlacement(null); setEditPlacementId(null); }} title={editPlacementId ? "Edit Placement" : "Tambah Placement"} testid="mkt-pfp-pl-modal"
             footer={<>
               <button onClick={() => { setFormPlacement(null); setEditPlacementId(null); }} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Batal</button>
               <button onClick={simpanPlacement} disabled={busy} data-testid="mkt-pfp-pl-simpan"
                       className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">{busy ? "Menyimpan..." : "Simpan"}</button>
             </>}>
        {formPlacement && (
          <>
            <Field label="Akun PFP" required>
              <select value={formPlacement.pfp_account_id} disabled={!!editPlacementId} onChange={(e) => setFormPlacement((f) => ({ ...f, pfp_account_id: e.target.value }))}
                      data-testid="mkt-pfp-pl-f-akun" className={selectCls}>
                <option value="">— pilih akun —</option>
                {akunRows.map((a) => <option key={a.id} value={a.id}>{a.nama_akun}</option>)}
              </select>
            </Field>
            {bolehCampaign && (
              <Field label="Campaign (opsional)">
                <select value={formPlacement.campaign_id} disabled={!!editPlacementId} onChange={(e) => setFormPlacement((f) => ({ ...f, campaign_id: e.target.value }))}
                        data-testid="mkt-pfp-pl-f-campaign" className={selectCls}>
                  <option value="">— tidak terkait campaign —</option>
                  {Object.values(campaignMap).map((c) => <option key={c.id} value={c.id}>{c.code} — {c.nama}</option>)}
                </select>
              </Field>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tanggal Tayang" required>
                <DatePicker value={formPlacement.tanggal_tayang} onChange={(v) => setFormPlacement((f) => ({ ...f, tanggal_tayang: v }))} data-testid="mkt-pfp-pl-f-tanggal" />
              </Field>
              <Field label="Nominal (Rp)" required>
                <InputRupiah value={formPlacement.nominal} onChange={(v) => setFormPlacement((f) => ({ ...f, nominal: v }))} testid="mkt-pfp-pl-f-nominal" className={inputCls} />
              </Field>
            </div>
            <Field label="Bukti (URL)">
              <input value={formPlacement.bukti_url} onChange={(e) => setFormPlacement((f) => ({ ...f, bukti_url: e.target.value }))} data-testid="mkt-pfp-pl-f-bukti" className={inputCls} placeholder="Link screenshot/post" />
            </Field>
            <Field label="Catatan">
              <textarea value={formPlacement.catatan} rows={2} onChange={(e) => setFormPlacement((f) => ({ ...f, catatan: e.target.value }))} data-testid="mkt-pfp-pl-f-catatan" className={textareaCls} />
            </Field>
          </>
        )}
      </Modal>

      {/* Modal bayar placement */}
      <Modal open={!!bayarState} onClose={() => setBayarState(null)} title="Bayar Placement" maxWidth="max-w-sm" testid="mkt-pfp-bayar-modal"
             footer={<>
               <button onClick={() => setBayarState(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Batal</button>
               <button onClick={submitBayar} disabled={busy} data-testid="mkt-pfp-bayar-submit"
                       className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">{busy ? "Memproses..." : "Bayar"}</button>
             </>}>
        {bayarState && (
          <>
            <p className="text-sm text-slate-600">{bayarState.placement.pfp_nama_akun} — {bayarState.placement.code}: <span className="font-mono font-semibold">{idr(bayarState.placement.nominal)}</span></p>
            <Field label="Dari Akun" required>
              <select value={bayarState.sumber_akun_id} onChange={(e) => setBayarState((s) => ({ ...s, sumber_akun_id: e.target.value }))} data-testid="mkt-pfp-bayar-akun" className={selectCls}>
                <option value="">Pilih akun...</option>
                {akunKasBank.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
              </select>
            </Field>
            <Field label="Tanggal">
              <DatePicker value={bayarState.tanggal} onChange={(v) => setBayarState((s) => ({ ...s, tanggal: v }))} data-testid="mkt-pfp-bayar-tanggal" />
            </Field>
          </>
        )}
      </Modal>

      {laporanTarget && (
        <LaporanPlacementModal placement={laporanTarget} user={user} onClose={() => setLaporanTarget(null)}
                               onSuccess={() => { setLaporanTarget(null); refetchPl(); }} />
      )}

      <PinHapusDialog dialog={pinDialog} setDialog={setPinDialog} onSubmit={() => submitDialog(() => { refetchAkun(); refetchPl(); })} />
    </div>
  );
}
